// Include shorthand families so changing a longhand does not lose an existing
// inline shorthand (or its priority) when the page is restored.
const BODY_PROPERTIES = ['position', 'inset', 'top', 'right', 'bottom', 'left', 'width']
const ROOT_PROPERTIES = [
  'overflow', 'overflow-x', 'overflow-y',
  'overscroll-behavior', 'overscroll-behavior-x', 'overscroll-behavior-y',
]
const SCROLL_BEHAVIOR = ['scroll-behavior']

const owners = new Set()
let snapshot = null

function captureStyles(style, properties) {
  return Array.from({ length: style.length }, (_, index) => style.item(index))
    .filter(property => properties.includes(property))
    .map(property => ({
      property,
      value: style.getPropertyValue(property),
      priority: style.getPropertyPriority(property),
    }))
}

function restoreStyles(style, properties, saved) {
  properties.forEach(property => style.removeProperty(property))
  saved.forEach(({ property, value, priority }) => style.setProperty(property, value, priority))
}

/**
 * Freeze the document while allowing a modal's own scroll containers to work.
 * Each caller owns one release function; repeated release calls are harmless.
 * Only the final owner restores the original page position and inline styles.
 */
export function acquirePageScrollLock() {
  if (typeof window === 'undefined' || typeof document === 'undefined'
    || !document.body || !document.documentElement) {
    return () => {}
  }

  if (owners.size === 0) {
    const root = document.documentElement
    const body = document.body
    snapshot = {
      window,
      root,
      body,
      x: window.scrollX ?? window.pageXOffset ?? 0,
      y: window.scrollY ?? window.pageYOffset ?? 0,
      rootStyles: captureStyles(root.style, ROOT_PROPERTIES),
      bodyStyles: captureStyles(body.style, BODY_PROPERTIES),
      scrollBehavior: captureStyles(root.style, SCROLL_BEHAVIOR),
    }

    // Inline priorities also cover pages whose stylesheet uses !important.
    root.style.setProperty('scroll-behavior', 'auto', 'important')
    root.style.setProperty('overflow', 'hidden', 'important')
    root.style.setProperty('overscroll-behavior', 'none', 'important')
    body.style.setProperty('position', 'fixed', 'important')
    body.style.setProperty('top', `${-snapshot.y}px`, 'important')
    body.style.setProperty('left', `${-snapshot.x}px`, 'important')
    body.style.setProperty('width', '100%', 'important')
  }

  const owner = Symbol('page-scroll-lock')
  owners.add(owner)

  return () => {
    if (!owners.delete(owner) || owners.size > 0) return
    const saved = snapshot
    snapshot = null

    // Keep smooth scrolling disabled until the old coordinates are restored.
    // Restore only properties we own, leaving unrelated style changes intact.
    saved.root.style.setProperty('scroll-behavior', 'auto', 'important')
    restoreStyles(saved.body.style, BODY_PROPERTIES, saved.bodyStyles)
    restoreStyles(saved.root.style, ROOT_PROPERTIES, saved.rootStyles)
    try {
      saved.window.scrollTo(saved.x, saved.y)
    } finally {
      restoreStyles(saved.root.style, SCROLL_BEHAVIOR, saved.scrollBehavior)
    }
  }
}
